import React, { useState, useRef, useCallback, useEffect } from 'react';
import { api } from '../../api/client';
import { pickGoogleDoc, fileIdFromUrl, isDrivePickerConfigured, preloadDrivePicker, isPopupBlocked, isDriveScopeDenied } from '../../utils/googleDrivePicker';

const NAVY = '#0C2D48';
const FONT = "'Raleway', sans-serif";

const PRIORITY_COLOR = { high: '#dc2626', medium: '#f59e0b', low: '#22c55e' };

// ── Small helpers ────────────────────────────────────────────────────────────
function PriorityBadge({ value, onChange }) {
  const colors = { high: '#dc2626', medium: '#f59e0b', low: '#22c55e' };
  const next    = { high: 'low', medium: 'high', low: 'medium' };
  return (
    <button
      onClick={() => onChange(next[value] || 'medium')}
      title="Click to change priority"
      style={{
        background: colors[value] || colors.medium, color: '#fff', border: 'none',
        borderRadius: 4, padding: '0.1rem 0.45rem', fontSize: '0.65rem', fontWeight: 700,
        cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0,
      }}
    >
      {value || 'medium'}
    </button>
  );
}

function countItems(sections) {
  let tasks = 0, subs = 0;
  (sections || []).forEach(s => {
    (s.tasks || []).forEach(t => { tasks++; subs += (t.subtasks || []).length; });
  });
  return { tasks, subs };
}

// ── Step 1: Input ────────────────────────────────────────────────────────────
function InputStep({ onPlanReady, onClose }) {
  const [tab, setTab]         = useState('upload'); // upload | text | googledoc
  const [file, setFile]       = useState(null);
  const [text, setText]       = useState('');
  const [gdUrl, setGdUrl]     = useState('');
  const [picked, setPicked]   = useState(null);   // { fileId, name, accessToken } from the Drive Picker
  const [picking, setPicking] = useState(false);
  const [projName, setProjName] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const fileRef = useRef();

  // Warm the Google scripts as soon as the import modal opens, so clicking "Choose from Google Drive"
  // has nothing async to wait for and the OAuth pop-up fires inside the click's user-gesture window.
  useEffect(() => { preloadDrivePicker(); }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) { setFile(f); if (!projName) setProjName(f.name.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' ')); }
  }, [projName]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) { setFile(f); if (!projName) setProjName(f.name.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' ')); }
  };

  async function submit() {
    setError(''); setLoading(true);
    try {
      const fd = new FormData();
      fd.append('projectName', projName || 'Imported Project');
      fd.append('startDate', startDate);
      if (tab === 'upload' && file) fd.append('file', file);
      else if (tab === 'text') fd.append('text', text);
      else if (tab === 'googledoc' && picked) {
        // Block 178: send the Picker-selected Doc + its drive.file token. The backend exports by
        // fileId with this short-lived token — no server-side Drive scope. This is the only Google
        // Doc path now; the pasted-URL server export (drive.readonly) was removed.
        fd.append('driveFileId', picked.fileId);
        fd.append('driveAccessToken', picked.accessToken);
        if (!projName && picked.name) fd.set('projectName', picked.name.replace(/\.[^.]+$/, ''));
      }
      else throw new Error('No input provided');

      // Enqueue the import — returns 202 + jobId immediately (async: the AI step
      // runs in the background so it never hits the ~30s CloudFront origin timeout).
      const res  = await api('/api/ai/import-document', { method: 'POST', body: fd });
      let data = {};
      try { data = await res.json(); } catch { /* non-JSON */ }
      if (!res.ok || !data.jobId) {
        throw new Error(data.error || `Import failed (server returned ${res.status}).`);
      }

      // Poll job status until the background worker finishes.
      const jobId = data.jobId;
      const started = Date.now();
      const MAX_MS = 150000; // 2.5 min hard stop on the client side
      while (true) {                                   // eslint-disable-line no-constant-condition
        await new Promise(r => setTimeout(r, 2000));
        if (Date.now() - started > MAX_MS) {
          throw new Error('Import is taking longer than expected. Please try again.');
        }
        let sdata = {};
        try {
          const sres = await api(`/api/ai/import-document/status/${jobId}`);
          sdata = await sres.json();
        } catch { continue; /* transient poll error — keep trying */ }
        if (sdata.status === 'done')  { onPlanReady(sdata.plan); break; }
        if (sdata.status === 'error') { throw new Error(sdata.error || 'Import failed'); }
        // status === 'processing' → keep polling
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  const canSubmit = (tab === 'upload' && file) || (tab === 'text' && text.trim()) || (tab === 'googledoc' && !!picked);

  // Open the Drive Picker. If the user pasted a URL, pre-navigate straight to that file.
  // Any failure (script hang→timeout, unauthorised origin, cancelled auth) surfaces a real message
  // pointing at the paste-link fallback below, and `finally` guarantees the button never sticks.
  async function chooseFromDrive() {
    setError(''); setPicking(true);
    try {
      const id = fileIdFromUrl(gdUrl);
      const result = await pickGoogleDoc({ fileIds: id ? [id] : undefined });
      if (result) setPicked(result); // null = user cancelled → leave prior selection untouched
    } catch (e) {
      setError(
        isPopupBlocked(e)
          ? 'Google could not open its sign-in window. Your browser may be blocking pop-ups for this site. Allow pop-ups and try again, or use the Upload file or Paste text tab instead.'
        : isDriveScopeDenied(e)
          ? 'Aidey needs permission to open the file you choose from Google Drive. When Google asks, tick the Google Drive box — or use the Upload file or Paste text tab instead.'
          : 'Could not open Google Drive. Use the Upload file or Paste text tab instead.');
    } finally {
      setPicking(false);
    }
  }

  const tabBtn = (id, label) => (
    <button key={id} onClick={() => setTab(id)} style={{
      padding: '0.45rem 1rem', border: 'none', borderBottom: tab === id ? `2.5px solid ${NAVY}` : '2.5px solid transparent',
      background: 'none', cursor: 'pointer', fontFamily: FONT, fontWeight: tab === id ? 700 : 500,
      fontSize: '0.875rem', color: tab === id ? NAVY : '#666', marginBottom: -1,
    }}>{label}</button>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb' }}>
        {tabBtn('upload', 'Upload file')}
        {tabBtn('text', 'Paste text')}
        {tabBtn('googledoc', 'Google Doc')}
      </div>

      {/* Tab content */}
      {tab === 'upload' && (
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
          style={{
            border: `2px dashed ${dragging ? NAVY : '#d1d5db'}`, borderRadius: 10,
            padding: '2.5rem 1.5rem', textAlign: 'center', cursor: 'pointer',
            background: dragging ? '#f0f4ff' : '#fafafa', transition: 'all 0.12s',
          }}
        >
          <input ref={fileRef} type="file" accept=".pdf,.docx,.txt" style={{ display: 'none' }} onChange={handleFileChange} />
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📄</div>
          {file ? (
            <div>
              <div style={{ fontWeight: 700, color: NAVY, fontSize: '0.9rem' }}>{file.name}</div>
              <div style={{ color: '#888', fontSize: '0.8rem', marginTop: 4 }}>{(file.size / 1024).toFixed(0)} KB · Click to change</div>
            </div>
          ) : (
            <div>
              <div style={{ fontWeight: 600, color: '#444', fontSize: '0.9rem' }}>Drag & drop or click to upload</div>
              <div style={{ color: '#aaa', fontSize: '0.8rem', marginTop: 4 }}>PDF, DOCX, or TXT · up to 20 MB</div>
            </div>
          )}
        </div>
      )}

      {tab === 'text' && (
        <textarea
          placeholder="Paste your project brief, scope of work, or planning notes here…"
          value={text}
          onChange={e => setText(e.target.value)}
          rows={10}
          style={{ width: '100%', boxSizing: 'border-box', padding: '0.75rem', borderRadius: 8, border: '1px solid #d1d5db', fontFamily: FONT, fontSize: '0.875rem', resize: 'vertical', outline: 'none' }}
        />
      )}

      {tab === 'googledoc' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {picked ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.75rem 0.9rem', borderRadius: 8, border: '1px solid #d1d5db', background: '#f8fafc' }}>
              <span style={{ fontSize: '1.15rem' }}>📄</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: NAVY, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{picked.name || 'Selected document'}</div>
                <div style={{ color: '#888', fontSize: '0.78rem', marginTop: 2 }}>Ready to import · access granted for this file only</div>
              </div>
              <button onClick={chooseFromDrive} disabled={picking} style={{ background: 'none', border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', fontFamily: FONT }}>Change</button>
            </div>
          ) : (
            <>
              <button
                onClick={chooseFromDrive}
                disabled={picking || !isDrivePickerConfigured()}
                style={{ padding: '0.7rem 1rem', borderRadius: 8, border: `1.5px solid ${NAVY}`, background: '#fff', color: NAVY, fontWeight: 700, fontFamily: FONT, fontSize: '0.9rem', cursor: picking ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
              >
                {picking ? 'Opening Google Drive…' : '📂 Choose from Google Drive'}
              </button>
              <input
                type="url"
                placeholder="Have a link? Paste it to jump straight to that file in the picker (optional)"
                value={gdUrl}
                onChange={e => setGdUrl(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', padding: '0.6rem 0.75rem', borderRadius: 8, border: '1px solid #d1d5db', fontFamily: FONT, fontSize: '0.83rem', outline: 'none' }}
              />
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#888' }}>Aidey only gets access to the file you pick. If the picker won’t open, use the Upload file or Paste text tab instead.</p>
            </>
          )}
        </div>
      )}

      {/* Project name + start date */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div style={{ flex: 2, minWidth: 180 }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#555', marginBottom: 4 }}>Project name</label>
          <input type="text" placeholder="Imported Project" value={projName} onChange={e => setProjName(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', padding: '0.6rem 0.75rem', borderRadius: 8, border: '1px solid #d1d5db', fontFamily: FONT, fontSize: '0.875rem', outline: 'none' }} />
        </div>
        <div style={{ flex: 1, minWidth: 140 }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#555', marginBottom: 4 }}>Start date</label>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', padding: '0.6rem 0.75rem', borderRadius: 8, border: '1px solid #d1d5db', fontFamily: FONT, fontSize: '0.875rem', outline: 'none' }} />
        </div>
      </div>

      {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '0.65rem 0.85rem', color: '#dc2626', fontSize: '0.85rem' }}>{error}</div>}

      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: '#f0f4ff', borderRadius: 8 }}>
          <div style={{ width: 20, height: 20, border: `3px solid ${NAVY}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite', flexShrink: 0 }} />
          <span style={{ fontWeight: 600, color: NAVY, fontSize: '0.9rem' }}>Aidey is reading your document…</span>
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 8, padding: '0.55rem 1.1rem', cursor: 'pointer', fontFamily: FONT, fontWeight: 600, fontSize: '0.875rem', color: '#555' }}>Cancel</button>
        <button onClick={submit} disabled={!canSubmit || loading}
          style={{ background: canSubmit && !loading ? NAVY : '#94a3b8', color: '#fff', border: 'none', borderRadius: 8, padding: '0.55rem 1.4rem', cursor: canSubmit && !loading ? 'pointer' : 'not-allowed', fontFamily: FONT, fontWeight: 700, fontSize: '0.875rem' }}>
          {loading ? 'Processing… (up to a minute)' : 'Import with Aidey'}
        </button>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Step 2: Preview ──────────────────────────────────────────────────────────
function PreviewStep({ plan: initialPlan, onConfirm, onBack }) {
  const [plan, setPlan] = useState(initialPlan);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const toggleSub = (taskKey) => setExpanded(e => ({ ...e, [taskKey]: !e[taskKey] }));

  const updatePlanName = (v) => setPlan(p => ({ ...p, projectName: v }));
  const updateSectionName = (si, v) => setPlan(p => {
    const sections = p.sections.map((s, i) => i === si ? { ...s, name: v } : s);
    return { ...p, sections };
  });
  const deleteSection = (si) => setPlan(p => ({ ...p, sections: p.sections.filter((_, i) => i !== si) }));
  const updateTask = (si, ti, field, v) => setPlan(p => {
    const sections = p.sections.map((s, i) => {
      if (i !== si) return s;
      const tasks = s.tasks.map((t, j) => j === ti ? { ...t, [field]: v } : t);
      return { ...s, tasks };
    });
    return { ...p, sections };
  });
  const deleteTask = (si, ti) => setPlan(p => {
    const sections = p.sections.map((s, i) => {
      if (i !== si) return s;
      return { ...s, tasks: s.tasks.filter((_, j) => j !== ti) };
    });
    return { ...p, sections };
  });

  async function confirm() {
    setError(''); setLoading(true);
    try {
      const res  = await api('/api/ai/import-document/confirm', { method: 'POST', body: JSON.stringify({ plan }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create project');
      onConfirm(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  const { tasks, subs } = countItems(plan.sections);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
      {/* Project name */}
      <div>
        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>Project name</label>
        <input value={plan.projectName} onChange={e => updatePlanName(e.target.value)}
          style={{ width: '100%', boxSizing: 'border-box', padding: '0.6rem 0.75rem', borderRadius: 8, border: `2px solid ${NAVY}`, fontFamily: "'Poppins', sans-serif", fontSize: '1.05rem', fontWeight: 700, color: NAVY, outline: 'none' }} />
      </div>

      {/* Summary pill */}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        {[`${(plan.sections || []).length} sections`, `${tasks} tasks`, `${subs} subtasks`].map(label => (
          <span key={label} style={{ background: '#f0f4ff', color: NAVY, fontSize: '0.78rem', fontWeight: 700, padding: '0.2rem 0.65rem', borderRadius: 20, border: `1px solid #c7d7f0` }}>{label}</span>
        ))}
      </div>

      {/* Sections tree */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxHeight: '52vh', overflowY: 'auto', paddingRight: 2 }}>
        {(plan.sections || []).map((section, si) => (
          <div key={si} style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
            {/* Section header */}
            <div style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb', padding: '0.55rem 0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input value={section.name} onChange={e => updateSectionName(si, e.target.value)}
                style={{ flex: 1, border: 'none', background: 'none', fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.875rem', color: NAVY, outline: 'none', minWidth: 0 }} />
              <button onClick={() => deleteSection(si)} title="Delete section" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: '1rem', lineHeight: 1, padding: '0 2px', flexShrink: 0 }}>×</button>
            </div>

            {/* Tasks */}
            <div style={{ padding: '0.4rem 0.5rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
              {(section.tasks || []).map((task, ti) => {
                const taskKey = `${si}-${ti}`;
                const hasSubs = (task.subtasks || []).length > 0;
                return (
                  <div key={ti} style={{ border: '1px solid #f0f0f0', borderRadius: 7, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.5rem', background: '#fff' }}>
                      {/* Subtask toggle */}
                      {hasSubs ? (
                        <button onClick={() => toggleSub(taskKey)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: '0.75rem', lineHeight: 1, padding: 0, width: 16, flexShrink: 0 }}>
                          {expanded[taskKey] ? '▾' : '▸'}
                        </button>
                      ) : <span style={{ width: 16, flexShrink: 0 }} />}

                      <input value={task.title} onChange={e => updateTask(si, ti, 'title', e.target.value)}
                        style={{ flex: 1, border: 'none', background: 'none', fontFamily: FONT, fontWeight: 600, fontSize: '0.83rem', color: '#222', outline: 'none', minWidth: 0 }} />

                      <PriorityBadge value={task.priority || 'medium'} onChange={v => updateTask(si, ti, 'priority', v)} />

                      {task.dueDate && (
                        <input type="date" value={task.dueDate.slice(0, 10)} onChange={e => updateTask(si, ti, 'dueDate', e.target.value)}
                          style={{ border: '1px solid #e5e7eb', borderRadius: 5, padding: '0.15rem 0.3rem', fontSize: '0.72rem', fontFamily: FONT, color: '#555', outline: 'none', flexShrink: 0 }} />
                      )}

                      <button onClick={() => deleteTask(si, ti)} title="Delete task" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ccc', fontSize: '0.9rem', lineHeight: 1, padding: '0 2px', flexShrink: 0 }}>×</button>
                    </div>

                    {/* Subtasks */}
                    {hasSubs && expanded[taskKey] && (
                      <div style={{ background: '#fafafa', borderTop: '1px solid #f0f0f0', padding: '0.3rem 0.5rem 0.3rem 2rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                        {(task.subtasks || []).map((sub, ki) => (
                          <div key={ki} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                            <span style={{ color: '#ccc', fontSize: '0.75rem' }}>└</span>
                            <span style={{ fontSize: '0.78rem', color: '#555', fontFamily: FONT }}>{sub.title}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {(section.tasks || []).length === 0 && (
                <p style={{ margin: 0, padding: '0.4rem 0.5rem', color: '#aaa', fontSize: '0.8rem', fontStyle: 'italic' }}>No tasks in this section</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '0.65rem 0.85rem', color: '#dc2626', fontSize: '0.85rem' }}>{error}</div>}

      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'space-between', alignItems: 'center' }}>
        <button onClick={onBack} style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 8, padding: '0.55rem 1rem', cursor: 'pointer', fontFamily: FONT, fontWeight: 600, fontSize: '0.875rem', color: '#555' }}>← Start over</button>
        <button onClick={confirm} disabled={loading}
          style={{ background: loading ? '#94a3b8' : NAVY, color: '#fff', border: 'none', borderRadius: 8, padding: '0.55rem 1.4rem', cursor: loading ? 'not-allowed' : 'pointer', fontFamily: FONT, fontWeight: 700, fontSize: '0.875rem' }}>
          {loading ? 'Creating…' : 'Create project'}
        </button>
      </div>
    </div>
  );
}

// ── Step 3: Confirmation ─────────────────────────────────────────────────────
function ConfirmStep({ result, onViewProject, onClose }) {
  const firstDue = result.firstDueDate
    ? new Date(result.firstDueDate).toLocaleDateString([], { day: 'numeric', month: 'long' })
    : null;

  return (
    <div style={{ textAlign: 'center', padding: '2rem 1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.25rem' }}>
      <div style={{ fontSize: '3rem' }}>🎉</div>
      <div>
        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.15rem', fontWeight: 800, color: NAVY, fontFamily: "'Poppins', sans-serif" }}>
          Done! {result.projectName} is ready.
        </h3>
        <p style={{ margin: 0, color: '#666', fontSize: '0.9rem', lineHeight: 1.6 }}>
          I created <strong>{result.sectionCount}</strong> section{result.sectionCount !== 1 ? 's' : ''},{' '}
          <strong>{result.totalTasks}</strong> task{result.totalTasks !== 1 ? 's' : ''}, and{' '}
          <strong>{result.totalSubtasks}</strong> subtask{result.totalSubtasks !== 1 ? 's' : ''}.
          {firstDue && <> Your first task is due <strong>{firstDue}</strong>.</>}
        </p>
      </div>
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button onClick={onClose} style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 8, padding: '0.55rem 1rem', cursor: 'pointer', fontFamily: FONT, fontWeight: 600, fontSize: '0.875rem', color: '#555' }}>Close</button>
        <button onClick={() => onViewProject(result.projectId)}
          style={{ background: NAVY, color: '#fff', border: 'none', borderRadius: 8, padding: '0.55rem 1.4rem', cursor: 'pointer', fontFamily: FONT, fontWeight: 700, fontSize: '0.875rem' }}>
          View project →
        </button>
      </div>
    </div>
  );
}

// ── Main DocumentImporter ────────────────────────────────────────────────────
export default function DocumentImporter({ onClose, onViewProject }) {
  const [step, setStep]     = useState('input'); // input | preview | done
  const [plan, setPlan]     = useState(null);
  const [result, setResult] = useState(null);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9000,
      background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem',
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 680,
        maxHeight: '92vh', overflowY: 'auto',
        padding: '1.75rem', boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
        fontFamily: FONT,
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: NAVY, fontFamily: "'Poppins', sans-serif" }}>
              {step === 'input'   && 'Import from document'}
              {step === 'preview' && 'Review your project plan'}
              {step === 'done'    && 'Project created'}
            </h2>
            {step === 'input' && <p style={{ margin: '0.25rem 0 0', color: '#888', fontSize: '0.83rem' }}>Upload a brief, SOW, or notes and Aidey will turn it into a structured project.</p>}
            {step === 'preview' && <p style={{ margin: '0.25rem 0 0', color: '#888', fontSize: '0.83rem' }}>Edit anything before creating. Click priority badges to change them.</p>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#aaa', padding: '0 4px', lineHeight: 1, flexShrink: 0 }}>×</button>
        </div>

        {/* Step indicator */}
        {step !== 'done' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
            {['input', 'preview'].map((s, i) => (
              <React.Fragment key={s}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', background: step === s ? NAVY : (step === 'done' || (s === 'input' && step === 'preview')) ? '#22c55e' : '#e5e7eb', color: '#fff', fontSize: '0.72rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {(step === 'preview' && s === 'input') ? '✓' : i + 1}
                </div>
                <span style={{ fontSize: '0.78rem', color: step === s ? NAVY : '#aaa', fontWeight: step === s ? 700 : 400 }}>
                  {s === 'input' ? 'Upload & configure' : 'Review & edit'}
                </span>
                {i < 1 && <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />}
              </React.Fragment>
            ))}
          </div>
        )}

        {step === 'input' && (
          <InputStep onPlanReady={p => { setPlan(p); setStep('preview'); }} onClose={onClose} />
        )}
        {step === 'preview' && (
          <PreviewStep plan={plan} onBack={() => setStep('input')} onConfirm={r => { setResult(r); setStep('done'); }} />
        )}
        {step === 'done' && (
          <ConfirmStep result={result} onViewProject={onViewProject} onClose={onClose} />
        )}
      </div>
    </div>
  );
}
